import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import Helmet from 'react-helmet';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { layoutServiceFactory } from './lib/layout-service-factory';
import config from './temp/config';
import Layout from './Layout';
import NotFound from './NotFound';
import store from './redux/store';
/* eslint-disable no-console */

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

class RouteHandler extends React.Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    // If we are not using SSR we have to load layout data
    if (!this.props.isSSR) {
      this.updateLayoutData();
    }
  }

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */

  updateLayoutData() {
    const language = this.getLanguage();

    if (!isEditorActive()) {
      let contentUrl = new URL(`${config.sitecoreApiHost}${config.endpoints.content}`);
      contentUrl.searchParams.append('item', this.props.route);
      contentUrl.searchParams.append('sc_apikey', config.sitecoreApiKey);
      contentUrl.searchParams.append('sc_site', config.jssAppName);
      contentUrl.searchParams.append('sc_lang', language);
      contentUrl.searchParams.append('tracking', true);

      fetch(contentUrl)
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          throw `Error getting content: ${res.status}: ${res.statusText}`;
        })
        .then(routeData => {
          if (this.redirectIfJourneyInvalid()) {
            this.props.updateSitecoreContext(routeData);
          }
        })
        .catch(err => {
          console.error(err);
          window.location.assign(config?.urls?.pageNotFound + '/');
        });
    } else {
      // instantiate the dictionary service.
      const layoutServiceInstance = layoutServiceFactory.create();

      // get the global route data and merge with route specific data
      // get the route data for the new route
      layoutServiceInstance.fetchLayoutData(this.props.route, language).then(routeData => {
        this.props.updateSitecoreContext(routeData);
      });
    }
  }

  getLanguage() {
    return this.props.language || this.props.sitecoreContext.language || config.defaultLanguage;
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.url;
    const newRoute = this.props.url;

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }

    // if in Sitecore editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLayoutData();
  }

  isValidRoute() {
    let currentPath = window.location.pathname;
    if (currentPath.endsWith('/')) {
      currentPath = currentPath.slice(0, -1);
    }

    const whitelistedUrls = [
      config?.urls?.pageNotFound,
      config?.urls?.firstPage,
      config?.urls?.retrieve,
      config?.urls?.serviceDown,
    ].filter(Boolean);

    const isWhitelisted = whitelistedUrls.some(configuredPath => {
      return (
        configuredPath && (currentPath === configuredPath || currentPath.endsWith(configuredPath))
      );
    });

    if (isWhitelisted) {
      return true;
    }
    if (isEditorActive() || process.env.NODE_ENV !== 'production') {
      return true;
    }

    return false;
  }

  redirectIfJourneyInvalid() {
    if (this.isValidRoute()) {
      return true;
    }

    const state = store.getState();
    const prospectDetails = state?.quoteData?.prospectDetails;
    const isValid = prospectDetails?.forename;

    if (!isValid) {
      window.location.assign(config?.urls?.firstPage + '/');
      return false;
    }
    return true;
  }

  createHelmet(route) {
    const pageOptions = route?.fields?.pageOptions[0] || route?.fields?.pageOptions;
    if (!pageOptions) {
      return null;
    }

    const helmetElements = [];

    if (pageOptions.fields?.scripts && pageOptions.fields.scripts.length > 0) {
      pageOptions.fields.scripts.forEach((script, index) => {
        const scriptContent = script.fields?.content?.value;
        if (scriptContent) {
          const wrappedContent = `try { ${scriptContent} } catch (e) { console.error(e); }`;
          helmetElements.push(
            <script
              key={`${index}-${script.id}`}
              type="text/javascript"
              async={script.fields?.isAsync?.value}
            >
              {wrappedContent}
            </script>
          );
        }
        if (!scriptContent && script.fields?.url?.value) {
          helmetElements.push(
            <script
              key={`${index}-${script.id}`}
              type="text/javascript"
              src={script.fields.url.value}
              async={script.fields?.isAsync?.value}
            ></script>
          );
        }
      });
    }

    return <Helmet>{helmetElements}</Helmet>;
  }

  render() {
    const layoutData = this.props.sitecoreContext;

    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    // `route` is null in case if route is not found
    if (layoutData.route === null) {
      return (
        <div>
          <Helmet>
            <title>{i18n.t('Page not found')}</title>
          </Helmet>
          <NotFound context={layoutData} />
        </div>
      );
    }

    const helmet = this.createHelmet(layoutData.route);

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!layoutData.route) {
      return <>{helmet}</>;
    }

    // Render the app's root structural layout
    return (
      <>
        {helmet}
        <Layout route={layoutData.route} />
      </>
    );
  }
}

RouteHandler.propTypes = {
  isSSR: PropTypes.bool,
  route: PropTypes.string,
  url: PropTypes.string,
  language: PropTypes.string,
  updateSitecoreContext: PropTypes.func,
  sitecoreContext: PropTypes.shape({
    route: PropTypes.object,
    language: PropTypes.string,
  }),
};

export default withSitecoreContext({ updatable: true })(RouteHandler);
